import React, { useState } from 'react';
import {
    Box,
    IconButton,
    InputBase,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core';
import { Check, Edit2, Trash2, X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useWarboxMessage } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import {
    canDeleteNoteConversation,
    canUpdateNoteConversation,
} from 'src/views/OmniChannel/utils/noteConversationPermissions';

type NoteMessageActionsProps = {
    messageId: string;
    messageText: string;
    textColor: string;
};

export const NoteMessageActions = ({
    messageId,
    messageText,
    textColor,
}: NoteMessageActionsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const { updateNoteMessage, deleteNoteMessage } = useWarboxMessage();

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(messageText);
    const [isSaving, setIsSaving] = useState(false);

    const canUpdate = canUpdateNoteConversation(user);
    const canDelete = canDeleteNoteConversation(user);
    const hasActions = canUpdate || canDelete;

    const handleStartEdit = () => {
        setDraft(messageText);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setDraft(messageText);
        setIsEditing(false);
    };

    const handleSave = async () => {
        const nextText = draft.trim();

        if (!nextText) return;

        setIsSaving(true);

        try {
            await updateNoteMessage(messageId, nextText);
            setIsEditing(false);
            enqueueSnackbar(t('SnackBar.NoteUpdated'), {
                variant: 'success',
            });
        } catch (error) {
            enqueueSnackbar(
                error instanceof Error ? error.message : t('Errors.ScoreError'),
                { variant: 'error' }
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(t('Chat.DeleteNoteConfirm'));

        if (!confirmed) return;

        setIsSaving(true);

        try {
            await deleteNoteMessage(messageId);
            enqueueSnackbar(t('SnackBar.NoteDeleted'), {
                variant: 'success',
            });
        } catch (error) {
            enqueueSnackbar(
                error instanceof Error ? error.message : t('Errors.ScoreError'),
                { variant: 'error' }
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (isEditing) {
        return (
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                <InputBase
                    multiline
                    fullWidth
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    disabled={isSaving}
                    style={{
                        color: textColor,
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 4,
                        padding: '4px 8px',
                        minWidth: 350,
                    }}
                />
                <Box display="flex" justifyContent="flex-end" gridGap={4}>
                    <Tooltip title={t('Chat.CancelEdit')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                disabled={isSaving}
                                style={{ color: textColor }}
                            >
                                <X size={16} color={theme.palette.error.main} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('Chat.SaveNote')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleSave}
                                disabled={isSaving || !draft.trim()}
                                style={{ color: textColor }}
                            >
                                <Check
                                    size={16}
                                    color={theme.palette.success.main}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
            <Typography variant="body1" style={{ color: textColor }}>
                {messageText || '-'}
            </Typography>

            {hasActions && (
                <Box display="flex" justifyContent="flex-end" gridGap={4}>
                    {canUpdate && (
                        <Tooltip title={t('Chat.EditNote')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleStartEdit}
                                    disabled={isSaving}
                                    style={{ color: textColor }}
                                >
                                    <Edit2
                                        size={14}
                                        color={theme.palette.primary.main}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {canDelete && (
                        <Tooltip title={t('Chat.DeleteNote')}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleDelete}
                                    disabled={isSaving}
                                    style={{ color: textColor }}
                                >
                                    <Trash2
                                        size={14}
                                        color={theme.palette.error.main}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}
        </Box>
    );
};
